import { Box, Button, Checkbox, CircularProgress, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Classroom, Enrollment } from "../../models/classroom";
import { ChevronLeft, ChevronRight, Clear, Download, MoreVert, PersonRemove, Refresh, Search } from "@mui/icons-material";
import UnEnrollmentDialog from "./unenrollment-dialog";
import { useClassroomActions } from "../../hooks/useClassroomActions";



interface StudentsTableProps {
    selectedClassroom: Classroom;
    fetchStudents: (reset?: boolean) => Promise<void>;
    tracker: Record<number, boolean>;
}

function StudentsTable({ selectedClassroom, fetchStudents, tracker }: StudentsTableProps) {
    const { unEnrollStudents } = useClassroomActions();

    const [filteredEnrollments, setFilteredEnrollments] = useState<Enrollment[]>([]);
    
    const [selectedRows, setSelectedRows] = useState<number[]>([]);

    const [searchQuery, setSearchQuery] = useState<string>('');

    const [refreshing, setRefreshing] = useState(false);
    const [loading, setLoading] = useState(false);
    
    const [anchorEl, setAnchorEl] = useState(null);
    
    const open = Boolean(anchorEl);
    
    const [unEnrollmentDialog, setUnenrollmentDialog] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    const totalPages = Math.ceil(filteredEnrollments.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedEnrollments = filteredEnrollments.slice(startIndex, startIndex + itemsPerPage);
    
    
    const handlePreviousPage = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };
    
    const handleNextPage = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedRows(paginatedEnrollments.map((row) => row.student_id));
        } else {
            setSelectedRows([]);
        }
    };

    const handleRowClick = (enrollment: Enrollment) => {
        const selectedIndex = selectedRows.indexOf(enrollment.student_id);
        let newSelected: number[] = [];

        if (selectedIndex === -1) {
            newSelected = [...selectedRows, enrollment.student_id];
        } else if (selectedIndex === 0) {
            newSelected = selectedRows.slice(1);
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = selectedRows.slice(0, -1);
        } else if (selectedIndex > 0) {
            newSelected = [
            ...selectedRows.slice(0, selectedIndex),
            ...selectedRows.slice(selectedIndex + 1),
            ];
        }

        setSelectedRows(newSelected);
    };

    const isRowSelected = (enrollment: Enrollment): boolean => {
        return selectedRows.includes(enrollment.student_id);
    };

    
    const handleMenuClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        });
    };
    
    const handleDownloadCSV = () => {
    const headers = ['Name', 'Email', 'Registration Number', 'Enrollment Date', 'Expiry Date'];
    const csvContent = [
        headers.join(','),
        ...(selectedClassroom?.enrollments || []).map(enrollment => [
        `"${enrollment.name || 'N/A'}"`,
        `"${enrollment.email || 'N/A'}"`,
        `"${enrollment.reg_number || 'N/A'}"`,
        `"${formatDate(enrollment.enrollment_date)}"`,
        selectedClassroom?.enrollment_type === 0 ? 'No Expiry' : `"${formatDate(enrollment.expiry_date)}"`
        ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `elms_${selectedClassroom?.name || 'classroom'}_students.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    };
    
      
    const handleUnenroll = async () => {
        await unEnrollStudents(selectedClassroom.id, selectedRows);
        setSelectedRows([]);
    };
    
    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };
    
    const handleSearch = () => {
        if (!searchQuery.trim()) {
            setFilteredEnrollments(selectedClassroom?.enrollments || []);
            return;
        }

        const query = searchQuery.toLowerCase().trim();

        const filtered = (selectedClassroom?.enrollments || []).filter((enrollment) => {
            const name = (enrollment.name || '').toLowerCase();
            const email = (enrollment.email || '').toLowerCase();
            const regNumber = (enrollment.reg_number || '').toLowerCase();
            return name.includes(query) ||
            email.includes(query) ||
            regNumber.includes(query);
        });
        
        setFilteredEnrollments(filtered);
    };


    const handleClearSearch = () => {
        setSearchQuery('');
        setFilteredEnrollments(selectedClassroom?.enrollments || []);
    };

    
    const handleSearchInputChange = (e: any) => {
        setSearchQuery(e.target.value);
    }

    const loadMoreStudents = async (reset?: boolean) => {
        if (loading || refreshing) return;
        if (reset) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        await fetchStudents(reset);

        if (reset) {
            setRefreshing(false);
        } else {
            setLoading(false);
            setCurrentPage(totalPages + 1);
        }
    }

    useEffect(() => {
        setFilteredEnrollments(selectedClassroom.enrollments!);
    }, [selectedClassroom.enrollments]);

    useEffect(() => {
        if (!searchQuery) handleClearSearch();
    }, [searchQuery]);

    const hasMore = selectedClassroom.id in tracker && tracker[selectedClassroom.id];

    return (
        <Paper
            elevation={1}
            sx={{ 
                borderRadius: 3, 
                border: '1px solid',
                borderColor: 'grey.300',
                overflow: 'hidden'
            }}
        >

            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: 1,
                    gap: 2
                }}
            >
                <TextField
                    placeholder="Search by email address, phone number, or user UID"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyPress={handleKeyPress}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Search sx={{ color: 'grey.400', fontSize: 20 }} />
                        </InputAdornment>
                        ),
                        endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleClearSearch}
                                edge="end"
                                size="small"
                                sx={{
                                    color: 'grey.400',
                                    '&:hover': {
                                    color: 'grey.600',
                                    bgcolor: 'transparent'
                                    }
                                }}
                            >
                                <Clear sx={{ fontSize: 18 }} />
                            </IconButton>
                        </InputAdornment>
                        ),
                    }}
                    sx={{
                        flexGrow: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: 'grey.100',
                            height: '40px',
                            '& fieldset': {
                                borderColor: 'grey.300'
                            },
                            '&:hover fieldset': {
                                borderColor: 'grey.400'
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main'
                            }
                        }
                    }}
                />

                <Stack direction="row">                   
                    {selectedRows.length > 0 && (
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<PersonRemove />}
                            onClick={() => setUnenrollmentDialog(true)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                borderRadius: 1
                            }}
                        >
                            Unenroll ({selectedRows.length})
                        </Button>
                    )}

                    <IconButton
                        onClick={() => loadMoreStudents(true)}
                    >
                        {refreshing ? <CircularProgress color="inherit" size={20}/> : <Refresh sx={{ fontSize: 20 }}/>}
                    </IconButton>      
                    
                    <IconButton
                        onClick={handleMenuClick}
                        sx={{ 
                            color: 'grey.600',
                            '&:hover': {
                                bgcolor: 'grey.100'
                            }
                        }}
                    >
                        <MoreVert sx={{ fontSize: 20 }} />
                    </IconButton>
                    
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem onClick={handleDownloadCSV}>
                            <ListItemIcon><Download fontSize="small" /></ListItemIcon>
                            <ListItemText>Export to CSV</ListItemText>
                        </MenuItem>
                    </Menu>

                </Stack>
            </Box>

            <TableContainer>  
                <Table>
                    <TableHead>
                        <TableRow sx={{  }}>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={selectedRows.length > 0 && selectedRows.length < paginatedEnrollments.length}
                                    checked={paginatedEnrollments.length > 0 && selectedRows.length === paginatedEnrollments.length}
                                    onChange={handleSelectAllClick}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                                Personal Details
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                                Registration Number
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                                Enrollment Date
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                                Expiry Date
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                    {paginatedEnrollments.map((enrollment) => {
                        const isSelected = isRowSelected(enrollment);
                        return (
                        <TableRow 
                            key={`${enrollment.id}-${enrollment.student_id}`}
                            sx={{
                                "&:hover": { 
                                    bgcolor: "grey.50"
                                },
                                bgcolor: isSelected ? 'primary.50' : 'inherit'
                            }}
                        >
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleRowClick(enrollment)}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell>
                                <Typography 
                                    variant="body2" 
                                    fontWeight={500}
                                    sx={{ color: 'grey.900', mb: 0.5 }}
                                >
                                    {enrollment.name || 'N/A'}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{ color: 'grey.600', display: 'block' }}
                                >
                                    {enrollment.email || 'N/A'}
                                </Typography>
                            
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{ color: 'grey.800' }}>
                                    {enrollment.reg_number}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{ color: 'grey.800' }}>
                                    {formatDate(enrollment.enrollment_date)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{ color: 'grey.800' }}>
                                    {selectedClassroom.enrollment_type === 1 ? formatDate(enrollment.expiry_date) : "N/A"}
                                </Typography>
                            </TableCell>
                        </TableRow>
                        );
                    })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 1,
                    backgroundColor: 'grey.100',
                    gap: 3
                }}
            >
            
                <Typography
                    variant="body2"
                    sx={{
                        cursor: !hasMore ? "not-allowed" : "pointer",
                        color: !hasMore ? "text.disabled" : "primary.main",
                        pointerEvents: !hasMore ? "none" : "auto",
                        opacity: !hasMore ? 0.6 : 1,
                    }}
                    onClick={() => loadMoreStudents()}
                >
                    {loading ? "loading..." : "Load more"}
                </Typography>
            
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        {((currentPage - 1) * itemsPerPage) + 1} – {Math.min(currentPage * itemsPerPage, filteredEnrollments.length)} of {filteredEnrollments.length}
                    </Typography>
        
                    <IconButton 
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                    >
                        <ChevronLeft />
                    </IconButton>
        
                    <IconButton 
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                    >
                        <ChevronRight />
                    </IconButton>
                </Box>
            </Box>

            <UnEnrollmentDialog
                open={unEnrollmentDialog}
                setOpen={setUnenrollmentDialog}
                onProceed={handleUnenroll}
            />
        </Paper>
    );
}

export default StudentsTable;